import { assumptionLabel } from "./assumptions.ts";
import type { Footprint, RoofvizPacket } from "./contract.ts";
import { roofVizDiagnostics } from "./diagnostics.ts";
import {
  DEFAULT_FASCIA_ID,
  FASCIAS,
  fasciaCrossSection,
  findFascia,
  type FasciaId,
  type FasciaSpec,
} from "./fascia.ts";
import {
  DEFAULT_GUTTER_ID,
  GUTTERS,
  findGutter,
  gutterCrossSection,
  type GutterId,
  type GutterSpec,
} from "./gutters.ts";
import {
  ADDITION_REQUEST_EVENT,
  SAMPLE_REQUEST_EVENT,
  additionInterestStorageKey,
  createAdditionInterest,
  createSampleRequest,
  parseAdditionInterest,
  parseSampleRequest,
  sampleRequestStorageKey,
  serializeInterest,
  type RoofAdditionInterestV1,
  type RoofSampleRequestV1,
} from "./interest.ts";
import {
  buildCostFactors,
  buildRoofAdditions,
  buildRoofStory,
  type RoofAdditionId,
  type RoofStoryChapter,
} from "./journey.ts";
import {
  DEFAULT_COLOUR_ID,
  DEFAULT_PROFILE_ID,
  PROFILES,
  calculateEstimateGuide,
  calculateRelativePrice,
  coloursForProfile,
  findColour,
  findProfile,
  formatGuideValue,
  type Colour,
  type Profile,
  type ProfileId,
} from "./materials.ts";
import { metalFinishForProfile, profileCrossSection } from "./metalFinish.ts";
import { POSTER_HEIGHT, POSTER_WIDTH, posterCaptionState } from "./poster.ts";
import {
  PREFERENCE_EVENT,
  createPreference,
  parsePreference,
  preferenceStorageKey,
  serializePreference,
  type RoofAppearancePreferenceV1,
} from "./preference.ts";
import { recommendProfile, type RecommendationContext } from "./recommendation.ts";
import { buildRoofInsights, type RoofInsightId } from "./roofInsights.ts";
import { footprintsFromPacket, RoofVizScene, type CardinalView } from "./scene.ts";
import { fallbackForValidation, validatePacketForRendering } from "./validation.ts";

export interface RoofVisualizerPresentation {
  /** Standalone owns page chrome/storage; embedded defers both to its host. */
  shell?: RoofVisualizerShell;
  northOffsetDeg?: number;
  assumedHeights?: boolean;
  assumedHeightLabel?: string;
  homeownerNote?: string;
  footprints?: Footprint[];
  gutterId?: GutterId;
  fasciaId?: FasciaId;
  /** Optional host-owned ambient motion. Defaults off and respects reduced motion. */
  autoRotate?: boolean;
  autoRotateSpeed?: number;
  /** Host can start directly in the settled roof view instead of the mapping reveal. */
  startInRoof?: boolean;
}

export interface AppearanceSelection {
  profile: ProfileId;
  colour: string;
}

export type RoofVisualizerShell = "standalone" | "embedded";
export type RoofVisualizerView = CardinalView | "perspective";

export type RoofVisualizerStatus =
  | { state: "preparing" }
  | { state: "ready" }
  | {
    state: "unavailable";
    reason: "no_packet" | "invalid_packet" | "unsupported_packet" | "webgl_unavailable" | "render_failed";
  };

export const ROOF_VISUALIZER_INTENT_TYPES = [
  "appearance_preference_stated",
  "sample_interest_stated",
  "addition_interest_stated",
] as const;

export type RoofVisualizerIntent =
  | {
    schema: "roofviz_intent_v1";
    type: "appearance_preference_stated";
    trackingId: string;
    preference: RoofAppearancePreferenceV1;
  }
  | {
    schema: "roofviz_intent_v1";
    type: "sample_interest_stated";
    trackingId: string;
    interest: RoofSampleRequestV1;
  }
  | {
    schema: "roofviz_intent_v1";
    type: "addition_interest_stated";
    trackingId: string;
    interest: RoofAdditionInterestV1;
  };

export interface RoofVisualizerSession {
  appearance: AppearanceSelection;
  gutter: GutterId;
  fascia?: FasciaId;
  view: RoofVisualizerView;
}

export interface MountRoofVisualizerOptions {
  root: HTMLElement;
  packet: RoofvizPacket | null;
  presentation?: RoofVisualizerPresentation;
  recommendation?: RecommendationContext;
  initialPreference?: RoofAppearancePreferenceV1;
  initialSession?: RoofVisualizerSession;
  onStatusChange?: (status: RoofVisualizerStatus) => void;
  onIntent?: (intent: RoofVisualizerIntent) => void;
  /** Compatibility callbacks. Prefer onIntent for new hosts. */
  onPreferenceConfirmed?: (preference: RoofAppearancePreferenceV1) => void;
  onSampleRequested?: (request: RoofSampleRequestV1) => void;
  onAdditionRequested?: (request: RoofAdditionInterestV1) => void;
}

export interface RoofVisualizerHandle {
  loadPacket(packet: RoofvizPacket | null, presentation?: RoofVisualizerPresentation): void;
  setAppearance(appearance: AppearanceSelection): void;
  setGutter(gutter: GutterId): void;
  setFascia(fascia: FasciaId): void;
  setView(view: CardinalView): void;
  startStory(): void;
  stopStory(): void;
  reset(): void;
  capturePoster(): Promise<Blob>;
  snapshot(): RoofVisualizerSession;
  destroy(): void;
}

const DEVICE_ONLY_MESSAGE = "Saved on this device only. This does not update your job.";
const HOST_OWNED_MESSAGE = DEVICE_ONLY_MESSAGE;
let mountSequence = 0;

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;",
  })[character] ?? character);
}

function colourHex(colour: Colour): string {
  return `#${colour.hex.toString(16).padStart(6, "0")}`;
}

function dimensionsText(profile: Profile): string {
  const parts: string[] = [];
  if (profile.dimensions.ribHeightMM !== undefined) parts.push(`${profile.dimensions.ribHeightMM} mm rib`);
  if (profile.dimensions.pitchMM !== undefined) parts.push(`${profile.dimensions.pitchMM} mm profile pitch`);
  if (profile.dimensions.trayWidthsMM !== undefined) parts.push(`${profile.dimensions.trayWidthsMM.join(" / ")} mm trays`);
  if (profile.dimensions.trayWidthRangeMM !== undefined) parts.push(`${profile.dimensions.trayWidthRangeMM.join("–")} mm project-selected pans`);
  return parts.join(" · ");
}

function sectionPath(points: Array<{ x: number; y: number }>, width = 178, height = 46): string {
  const minX = Math.min(...points.map((point) => point.x));
  const maxX = Math.max(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxY = Math.max(...points.map((point) => point.y));
  const xSpan = Math.max(maxX - minX, 0.0001);
  const ySpan = Math.max(maxY - minY, 0.0001);
  return points.map((point, index) => {
    const x = 1 + ((point.x - minX) / xSpan) * (width - 2);
    const y = height - 1 - ((point.y - minY) / ySpan) * (height - 8);
    return `${index === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(" ");
}

function profileDiagram(profile: Profile): string {
  const spec = metalFinishForProfile(profile.id);
  const points = profileCrossSection(spec).map((point) => ({ x: point.offsetM, y: point.heightM }));
  return `<svg viewBox="0 0 180 48" aria-hidden="true"><path d="${sectionPath(points)}"/></svg>`;
}

function gutterDiagram(gutter: GutterSpec): string {
  const points = gutterCrossSection(gutter).map((point) => ({ x: point.outwardM, y: point.verticalM }));
  return `<svg viewBox="0 0 180 48" aria-hidden="true"><path d="${sectionPath(points)}"/></svg>`;
}

function fasciaDiagram(fascia: FasciaSpec): string {
  const points = fasciaCrossSection(fascia).map((point) => ({ x: point.outwardM, y: point.verticalM }));
  return `<svg viewBox="0 0 180 48" aria-hidden="true"><path d="${sectionPath(points)}"/></svg>`;
}

function validInitialPreference(
  preference: RoofAppearancePreferenceV1 | undefined,
  packet: RoofvizPacket,
): RoofAppearancePreferenceV1 | null {
  if (!preference || preference.propertyTrackingId !== packet.property.trackingId) return null;
  if (!findProfile(preference.profile) || !findColour(preference.colour)) return null;
  return preference;
}

function validInitialSession(session: RoofVisualizerSession | undefined): RoofVisualizerSession | null {
  if (!session) return null;
  const sessionProfile = findProfile(session.appearance.profile);
  const sessionColour = findColour(session.appearance.colour);
  const sessionGutter = findGutter(session.gutter);
  const sessionFascia = findFascia(session.fascia ?? DEFAULT_FASCIA_ID);
  if (!sessionProfile || !sessionColour || !sessionProfile.supportedColourIds.includes(sessionColour.id) || !sessionGutter || !sessionFascia) {
    return null;
  }
  if (!["perspective", "north", "east", "south", "west"].includes(session.view)) return null;
  return session;
}

export function mountRoofVisualizer(options: MountRoofVisualizerOptions): RoofVisualizerHandle {
  const { root } = options;
  let destroyed = false;
  let packet: RoofvizPacket | null = options.packet;
  const shell: RoofVisualizerShell = options.presentation?.shell ?? "standalone";
  let presentation: RoofVisualizerPresentation = { ...options.presentation, shell };
  let initialSessionToRestore = validInitialSession(options.initialSession);
  let scene: RoofVizScene | null = null;
  let currentCanvas: HTMLCanvasElement | null = null;
  let profile = findProfile(DEFAULT_PROFILE_ID)!;
  let colour = findColour(DEFAULT_COLOUR_ID)!;
  let gutter = findGutter(initialSessionToRestore?.gutter ?? presentation.gutterId ?? DEFAULT_GUTTER_ID)!;
  let fascia = findFascia(initialSessionToRestore?.fascia ?? presentation.fasciaId ?? DEFAULT_FASCIA_ID)!;
  let currentView: RoofVisualizerView = initialSessionToRestore?.view ?? "perspective";
  let catalogueExpanded = false;
  let gutterCatalogueExpanded = false;
  let focus: RoofInsightId = "roof";
  let confirmedPreference: RoofAppearancePreferenceV1 | null = null;
  let sampleRequest: RoofSampleRequestV1 | null = null;
  const additionInterests = new Map<RoofAdditionId, RoofAdditionInterestV1>();
  let recommendation = recommendProfile(options.recommendation);
  let storyChapters: RoofStoryChapter[] = [];
  let storyIndex = 0;
  let storyActive = false;
  let storyPaused = false;
  let storyTimer: number | null = null;
  const timers = new Set<number>();
  const abortController = new AbortController();
  let listenerCount = 0;
  let lastStatusKey = "";
  const canvasDescriptionId = `roofviz-canvas-description-${++mountSequence}`;

  roofVizDiagnostics.mounted(1);
  root.classList.add("roofviz-app", `rv-shell-${shell}`);
  root.innerHTML = `
    ${shell === "standalone" ? `<header class="rv-header">
      <a class="rv-brand" href="https://www.roof.co.nz/" target="_blank" rel="noreferrer" aria-label="Roofing Industries website">
        <span class="rv-brand-mark" aria-hidden="true">
          <span class="rv-brand-roofing">Roofing</span>
          <span class="rv-brand-industries">Industries</span>
        </span>
        <span class="rv-brand-product">Roof Visualizer</span>
      </a>
      <div class="rv-property">
        <span>Your roof</span>
        <strong data-rv="property">Preparing roof…</strong>
      </div>
    </header>` : ""}
    <main class="rv-workspace">
      <section class="rv-visual" aria-label="Interactive roof visualisation">
        <div class="rv-visual-stage" data-rv="stage"></div>
        <p class="rv-sr-only" id="${canvasDescriptionId}" data-rv="canvas-description">Preparing the mapped roof view.</p>
        <section class="rv-roof-intelligence" data-rv="roof-intelligence" aria-label="Explore this roof"></section>
        <aside class="rv-feature-note" data-rv="feature-note" hidden aria-live="polite"></aside>
        <button type="button" class="rv-story-launch" data-action="story-start">Walk me through my roof</button>
        <section class="rv-story" data-rv="story" aria-label="Guided roof story" hidden></section>
        <div class="rv-view-controls" aria-label="Roof view controls">
          <div class="rv-cardinal" role="group" aria-label="View from">
            <button type="button" data-action="view" data-view="north" aria-label="View from north" aria-pressed="${currentView === "north"}">N</button>
            <button type="button" data-action="view" data-view="east" aria-label="View from east" aria-pressed="${currentView === "east"}">E</button>
            <button type="button" data-action="view" data-view="south" aria-label="View from south" aria-pressed="${currentView === "south"}">S</button>
            <button type="button" data-action="view" data-view="west" aria-label="View from west" aria-pressed="${currentView === "west"}">W</button>
          </div>
          <div class="rv-zoom" role="group" aria-label="Zoom and reset">
            <button type="button" data-action="zoom-in" aria-label="Zoom in">+</button>
            <button type="button" data-action="zoom-out" aria-label="Zoom out">−</button>
            <button type="button" data-action="reset" class="rv-reset">Reset</button>
          </div>
          ${shell === "standalone" ? `<div class="rv-capture" role="group" aria-label="Save view">
            <button type="button" data-action="save-view" class="rv-save-view" aria-label="Save a picture of this view">Save view</button>
          </div>` : ""}
        </div>
        <div class="rv-visual-note"><span aria-hidden="true">↔</span> Drag gently to rotate · scroll or pinch to zoom</div>
      </section>
      <aside class="rv-decision" aria-label="Roof material selection">
        <div class="rv-decision-scroll">
          <div class="rv-eyebrow">Choose the look</div>
          ${shell === "standalone" ? "<h1>See what suits your roof.</h1>" : "<h2 class=\"rv-decision-title\">See what suits your roof.</h2>"}
          <p class="rv-intro">Explore the Roofing Industries residential range on the same mapped roof. Your measured roof stays unchanged.</p>
          <div class="rv-assumption" data-rv="assumption" hidden></div>
          <section class="rv-recommendation" data-rv="recommendation"></section>
          <section class="rv-section rv-profile-choice" aria-labelledby="rv-profile-title">
            <div class="rv-section-head">
              <div><span class="rv-mobile-kicker">Choose the look</span><h2 id="rv-profile-title">Roof profile</h2></div>
              <span><b class="rv-desktop-only">12 RI options</b><b class="rv-mobile-only">Recommended first · swipe</b></span>
            </div>
            <div class="rv-products" data-rv="products"></div>
            <button type="button" class="rv-catalogue-toggle" data-action="toggle-profiles" data-rv="profile-toggle">Explore all 12 RI profiles</button>
          </section>
          <section class="rv-profile-detail" data-rv="profile-detail"></section>
          <section class="rv-section rv-colour-choice" aria-labelledby="rv-colour-title">
            <div class="rv-section-head"><h2 id="rv-colour-title">Colour</h2><span data-rv="colour-name"></span></div>
            <div class="rv-colours" data-rv="colours"></div>
            <p class="rv-indicative">Colour on screen is indicative. Confirm the final product and colour with physical samples.</p>
          </section>
          <div class="rv-mobile-confirm-wrap">
            <button type="button" class="rv-confirm" data-action="confirm">Confirm this preference</button>
            <p class="rv-save-note" data-rv="save-note">This records a preference in this browser only.</p>
          </div>
          <div class="rv-sample-request" data-rv="sample-request"></div>
          ${shell === "standalone" ? `<button type="button" class="rv-mobile-save-poster" data-action="save-view">Save this roof view</button>` : ""}
          <section class="rv-section rv-gutter-choice" aria-labelledby="rv-gutter-title">
            <div class="rv-section-head"><h2 id="rv-gutter-title">Gutter &amp; spouting</h2><span>RI visual range</span></div>
            <div class="rv-gutters" data-rv="gutters"></div>
            <button type="button" class="rv-catalogue-toggle" data-action="toggle-gutters" data-rv="gutter-toggle">Explore all 13 gutter options</button>
            <div class="rv-gutter-detail" data-rv="gutter-detail"></div>
          </section>
          <section class="rv-section rv-fascia-choice" aria-labelledby="rv-fascia-title">
            <div class="rv-section-head"><h2 id="rv-fascia-title">Fascia finish</h2><span>Multifit™ system</span></div>
            <div class="rv-fascias" data-rv="fascias"></div>
            <div class="rv-fascia-detail" data-rv="fascia-detail"></div>
          </section>
          <section class="rv-price" data-rv="price"></section>
          <section class="rv-cost-factors" data-rv="cost-factors"></section>
          <section class="rv-section rv-additions-section" aria-labelledby="rv-additions-title">
            <div class="rv-section-head"><h2 id="rv-additions-title">Explore additions</h2><span>Assessment first</span></div>
            <div class="rv-additions" data-rv="additions"></div>
          </section>
          <p class="rv-visual-disclosure">Digital visualisation only. Screen colour, sheen, profile scale, junctions and accessories are indicative. Confirm the final product, colour and installation details with physical samples and your roofing team.</p>
        </div>
        <div class="rv-confirm-wrap">
          <button type="button" class="rv-confirm" data-action="confirm">Confirm this preference</button>
          <p class="rv-save-note" data-rv="save-note">${shell === "standalone" ? "This records a preference in this browser only." : "This states a preference to your Project Room without changing the job."}</p>
        </div>
      </aside>
    </main>`;

  const query = <T extends HTMLElement>(selector: string): T => {
    const element = root.querySelector<T>(selector);
    if (!element) throw new Error(`RoofViz mount element missing: ${selector}`);
    return element;
  };

  function syncViewControls(): void {
    root.querySelectorAll<HTMLButtonElement>("[data-action='view']").forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.view === currentView));
    });
  }

  function emitStatus(status: RoofVisualizerStatus): void {
    const key = JSON.stringify(status);
    if (key === lastStatusKey) return;
    lastStatusKey = key;
    try { options.onStatusChange?.(status); } catch { /* host callbacks cannot break the visualiser */ }
  }

  function readDeviceItem(key: string): string | null {
    if (shell !== "standalone") return null;
    try { return window.localStorage.getItem(key); } catch { return null; }
  }

  function writeDeviceItem(key: string, value: string): void {
    if (shell !== "standalone") return;
    try { window.localStorage.setItem(key, value); } catch { /* standalone still works without storage */ }
  }

  function removeDeviceItem(key: string): void {
    if (shell !== "standalone") return;
    try { window.localStorage.removeItem(key); } catch { /* standalone still works without storage */ }
  }

  function emitIntent(intent: RoofVisualizerIntent): void {
    if (options.onIntent) {
      try { options.onIntent(intent); } catch { /* host callbacks cannot break local UI state */ }
      return;
    }
    if (intent.type === "appearance_preference_stated") {
      try { options.onPreferenceConfirmed?.(intent.preference); } catch { /* compatibility callback */ }
      root.dispatchEvent(new CustomEvent<RoofAppearancePreferenceV1>(PREFERENCE_EVENT, {
        bubbles: true,
        composed: true,
        detail: intent.preference,
      }));
      return;
    }
    if (intent.type === "sample_interest_stated") {
      try { options.onSampleRequested?.(intent.interest); } catch { /* compatibility callback */ }
      root.dispatchEvent(new CustomEvent<RoofSampleRequestV1>(SAMPLE_REQUEST_EVENT, {
        bubbles: true,
        composed: true,
        detail: intent.interest,
      }));
      return;
    }
    try { options.onAdditionRequested?.(intent.interest); } catch { /* compatibility callback */ }
    root.dispatchEvent(new CustomEvent<RoofAdditionInterestV1>(ADDITION_REQUEST_EVENT, {
      bubbles: true,
      composed: true,
      detail: intent.interest,
    }));
  }

  function listen<K extends keyof HTMLElementEventMap>(
    target: HTMLElement,
    type: K,
    handler: (event: HTMLElementEventMap[K]) => void,
  ): void {
    target.addEventListener(type, handler as EventListener, { signal: abortController.signal });
    listenerCount += 1;
    roofVizDiagnostics.listener(1);
  }

  function clearTimers(): void {
    timers.forEach((timer) => window.clearTimeout(timer));
    timers.clear();
    storyTimer = null;
  }

  function schedule(callback: () => void, delay: number): void {
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      if (!destroyed) callback();
    }, delay);
    timers.add(timer);
  }

  function clearStoryTimer(): void {
    if (storyTimer === null) return;
    window.clearTimeout(storyTimer);
    timers.delete(storyTimer);
    storyTimer = null;
  }

  function renderFallback(
    title: string,
    message: string,
    reason: Extract<RoofVisualizerStatus, { state: "unavailable" }>["reason"],
  ): void {
    root.classList.add("rv-is-unavailable");
    const stage = query<HTMLElement>("[data-rv='stage']");
    stage.dataset.phase = "fallback";
    stage.innerHTML = `<div class="rv-fallback"><span aria-hidden="true">⌁</span><h2>${escapeHtml(title)}</h2><p>${escapeHtml(message)}</p></div>`;
    query<HTMLElement>("[data-rv='canvas-description']").textContent = `${title}. ${message}`;
    emitStatus({ state: "unavailable", reason });
  }

  function renderBlueprint(currentPacket: RoofvizPacket): string {
    const points = currentPacket.planes.flatMap((plane) => plane.ring);
    const xs = points.map((point) => point[0]);
    const ys = points.map((point) => point[1]);
    const padding = 1.5;
    const x = Math.min(...xs) - padding;
    const y = Math.min(...ys) - padding;
    const width = Math.max(...xs) - Math.min(...xs) + padding * 2;
    const height = Math.max(...ys) - Math.min(...ys) + padding * 2;
    const paths = currentPacket.lines.map((line) => {
      const path = line.polyline.map((point, index) => `${index === 0 ? "M" : "L"}${point[0]} ${point[1]}`).join(" ");
      return `<path class="rv-plan-line rv-plan-${line.class}" d="${path}" pathLength="1"/>`;
    }).join("");
    const pitchValues = [...new Set(currentPacket.planes.map((plane) => plane.pitchDeg))];
    const pitch = pitchValues.length === 1 ? `${pitchValues[0].toFixed(1)}°` : "Multiple pitches";
    const junctions = currentPacket.aggregates.ridgeM + currentPacket.aggregates.hipM + currentPacket.aggregates.valleyM;
    return `<div class="rv-blueprint" data-rv="blueprint">
      <div class="rv-blueprint-label"><span>Mapped roof</span><strong>Building your 3D view</strong></div>
      <svg viewBox="${x} ${y} ${width} ${height}" role="img" aria-label="Roof plan drawing"><g transform="translate(0 ${y * 2 + height}) scale(1 -1)">${paths}</g></svg>
      <div class="rv-blueprint-facts" aria-label="Mapped roof facts">
        <div><span>Roof surface</span><strong>${currentPacket.aggregates.surfaceAreaM2.toFixed(0)} m²</strong></div>
        <div><span>Roof planes</span><strong>${currentPacket.planes.length}</strong></div>
        <div><span>Pitch</span><strong>${pitch}</strong></div>
        <div><span>Junctions</span><strong>${junctions.toFixed(1)} m</strong></div>
      </div>
    </div>`;
  }

  function clearConfirmation(removeStored = true): void {
    confirmedPreference = null;
    if (packet && removeStored) {
      removeDeviceItem(preferenceStorageKey(packet.property.trackingId));
    }
    root.querySelectorAll<HTMLButtonElement>("[data-action='confirm']").forEach((button) => {
      button.classList.remove("is-confirmed");
      button.textContent = `Confirm ${profile.name} · ${colour.name}`;
    });
    root.querySelectorAll<HTMLElement>("[data-rv='save-note']").forEach((note) => {
      note.textContent = shell === "standalone"
        ? "This records a preference in this browser only."
        : "This states a preference to your Project Room without changing the job.";
    });
  }

  function renderConfirmation(): void {
    const isConfirmed = Boolean(confirmedPreference && confirmedPreference.profile === profile.id && confirmedPreference.colour === colour.id);
    root.querySelectorAll<HTMLButtonElement>("[data-action='confirm']").forEach((button) => {
      button.classList.toggle("is-confirmed", isConfirmed);
      button.textContent = isConfirmed ? `✓ ${profile.name} · ${colour.name}` : `Confirm ${profile.name} · ${colour.name}`;
    });
    root.querySelectorAll<HTMLElement>("[data-rv='save-note']").forEach((note) => {
      note.textContent = isConfirmed
        ? shell === "standalone" ? DEVICE_ONLY_MESSAGE : HOST_OWNED_MESSAGE
        : shell === "standalone"
          ? "This records a preference in this browser only."
          : "This states a preference to your Project Room without changing the job.";
    });
  }

  function renderRecommendation(): void {
    const recommended = findProfile(recommendation.recommendedProfileId)!;
    const sourceLabel = recommendation.source === "operator" ? "Recommended by your roofing team" : "Recommended starting point";
    query<HTMLElement>("[data-rv='recommendation']").innerHTML = `
      <div><span>${sourceLabel}</span><strong>${escapeHtml(recommended.name)}</strong></div>
      <button type="button" data-action="profile" data-profile="${recommended.id}">View on roof</button>`;
  }

  function renderProducts(): void {
    const visible = catalogueExpanded
      ? PROFILES
      : PROFILES.filter((product) => product.catalogueTier === "featured" || product.id === profile.id);
    query<HTMLElement>("[data-rv='products']").innerHTML = visible.map((product) => {
      const isRecommended = product.id === recommendation.recommendedProfileId;
      const isArchitecturalAlternative = product.id === "eurostyle_spanlok" && recommendation.recommendedProfileId !== product.id;
      return `<button type="button" class="rv-product${product.id === profile.id ? " is-selected" : ""}" data-action="profile" data-profile="${product.id}" aria-pressed="${product.id === profile.id}">
        <span class="rv-profile-sample rv-sample-${product.id}" aria-hidden="true"></span>
        <span><b>${escapeHtml(product.name)}</b><small>${escapeHtml(product.family)}</small></span>
        ${isRecommended ? '<em>Recommended</em>' : isArchitecturalAlternative ? '<em>Architectural</em>' : product.catalogueTier === "consultation" ? '<em>RI consultation</em>' : ""}
      </button>`;
    }).join("");
    const toggle = query<HTMLButtonElement>("[data-rv='profile-toggle']");
    toggle.textContent = catalogueExpanded ? "Show recommended profiles" : "Explore all 12 RI profiles";
    toggle.setAttribute("aria-expanded", String(catalogueExpanded));
  }

  function renderProfileDetail(): void {
    const relative = packet ? calculateRelativePrice(packet.aggregates, profile, findProfile(DEFAULT_PROFILE_ID)!) : null;
    query<HTMLElement>("[data-rv='profile-detail']").innerHTML = `
      <details class="rv-profile-disclosure">
        <summary><span>Why this profile</span><strong>${escapeHtml(profile.name)} · ${escapeHtml(relative?.label ?? "")}</strong></summary>
        <div class="rv-profile-detail-body">
          <div class="rv-detail-head"><span>${escapeHtml(profile.character)}</span><strong>${escapeHtml(relative?.label ?? "")}</strong></div>
          <p>${escapeHtml(profile.summary)}</p>
          <div class="rv-profile-section">
            <div class="rv-section-drawing">${profileDiagram(profile)}</div>
            <div><span>Profile at a glance</span><strong>${escapeHtml(dimensionsText(profile))}</strong></div>
          </div>
          <div class="rv-installed-look">
            <div><span>Sheet direction</span><strong>${escapeHtml(profile.installedLook.sheetDirection)}</strong></div>
            <div><span>Fixing look</span><strong>${escapeHtml(profile.installedLook.fixing)}</strong></div>
            <div><span>Ridge &amp; hip finish</span><strong>${escapeHtml(profile.installedLook.ridgeAndHip)}</strong></div>
          </div>
          <ul>${profile.reasons.slice(0, 3).map((reason) => `<li>${escapeHtml(reason)}</li>`).join("")}</ul>
          ${profile.presentationNote ? `<p class="rv-product-note"><strong>Visual basis:</strong> ${escapeHtml(profile.presentationNote)}</p>` : ""}
          ${profile.availabilityNote ? `<p class="rv-product-note"><strong>Availability:</strong> ${escapeHtml(profile.availabilityNote)}</p>` : ""}
          <div class="rv-product-facts"><span>Appearance modelled from published RI profile dimensions.</span><a href="${profile.sourceUrl}" target="_blank" rel="noreferrer">Official product details ↗</a></div>
        </div>
      </details>`;
  }

  function renderColours(): void {
    const available = coloursForProfile(profile);
    if (!available.some((item) => item.id === colour.id)) colour = available[0];
    query<HTMLElement>("[data-rv='colour-name']").textContent = colour.name;
    query<HTMLElement>("[data-rv='colours']").innerHTML = available.map((item) => `
      <button type="button" class="rv-colour${item.id === colour.id ? " is-selected" : ""}" data-action="colour" data-colour="${item.id}" aria-pressed="${item.id === colour.id}" aria-label="${escapeHtml(item.name)}">
        <i style="--swatch:${colourHex(item)}"></i><span>${escapeHtml(item.name)}</span>
      </button>`).join("");
  }

  function renderSampleRequest(): void {
    const current = sampleRequest?.profile === profile.id && sampleRequest.colour === colour.id;
    query<HTMLElement>("[data-rv='sample-request']").innerHTML = `
      <div><span>See the finish in person</span><strong>${escapeHtml(profile.name)} · ${escapeHtml(colour.name)}</strong></div>
      <button type="button" data-action="sample-request" class="${current ? "is-saved" : ""}">${current ? "✓ Sample interest saved" : "Prepare a sample request"}</button>
      <p>${current
        ? shell === "standalone"
          ? "Saved on this device only. No sample has been ordered yet."
          : "Sample interest stated. No sample has been ordered and your job has not changed."
        : "A physical sample is the final check for colour, sheen and finish."}</p>`;
  }

  function renderGutters(): void {
    const visible = gutterCatalogueExpanded
      ? GUTTERS
      : GUTTERS.filter((item) => item.catalogueTier === "featured" || item.id === gutter.id);
    query<HTMLElement>("[data-rv='gutters']").innerHTML = visible.map((item) => `
      <button type="button" class="rv-gutter${item.id === gutter.id ? " is-selected" : ""}" data-action="gutter" data-gutter="${item.id}" aria-pressed="${item.id === gutter.id}">
        <span class="rv-gutter-drawing">${gutterDiagram(item)}</span>
        <span><b>${escapeHtml(item.name)}</b><small>${escapeHtml(item.family)}</small></span>
      </button>`).join("");
    const toggle = query<HTMLButtonElement>("[data-rv='gutter-toggle']");
    toggle.textContent = gutterCatalogueExpanded ? "Show recommended gutter finishes" : "Explore all 13 gutter options";
    toggle.setAttribute("aria-expanded", String(gutterCatalogueExpanded));
    const capacity = gutter.fullCrossSectionMm2 === undefined
      ? "Capacity to be checked"
      : `${gutter.fullCrossSectionMm2.toLocaleString("en-NZ")} mm² published full section`;
    query<HTMLElement>("[data-rv='gutter-detail']").innerHTML = `
      <div><span>${escapeHtml(gutter.character)}</span><strong>${escapeHtml(capacity)}</strong></div>
      <p class="rv-basis">${escapeHtml(gutter.geometryBasis)}</p>
      <p>Shown colour-matched for appearance only. Roofing Industries confirms capacity, gradient, outlets, downpipes and local availability for the job.</p>
      ${gutter.availabilityNote ? `<p>${escapeHtml(gutter.availabilityNote)}</p>` : ""}
      <a href="${gutter.sourceUrl}" target="_blank" rel="noreferrer">Official rainwater details ↗</a>`;
  }

  function renderFascias(): void {
    const fasciaDimensions = fascia.coverHeightM === undefined
      ? `${Math.round(fascia.heightM * 1000)} mm product height`
      : `${Math.round(fascia.heightM * 1000)} mm overall · ${Math.round(fascia.coverHeightM * 1000)} mm cover`;
    query<HTMLElement>("[data-rv='fascias']").innerHTML = FASCIAS.map((item) => `
      <button type="button" class="rv-fascia${item.id === fascia.id ? " is-selected" : ""}" data-action="fascia" data-fascia="${item.id}" aria-pressed="${item.id === fascia.id}">
        <span class="rv-fascia-drawing">${fasciaDiagram(item)}</span>
        <span><b>${escapeHtml(item.name)}</b><small>${escapeHtml(item.family)}</small></span>
      </button>`).join("");
    query<HTMLElement>("[data-rv='fascia-detail']").innerHTML = `
      <div><span>${escapeHtml(fascia.character)}</span><strong>${escapeHtml(fasciaDimensions)} · ${fascia.attachment}</strong></div>
      <p class="rv-basis">${escapeHtml(fascia.geometryBasis)}</p>
      <p>Shown as a colour-related roof-edge finish. Roofing Industries confirms the attachment system, substrate, exact colour and gutter compatibility.</p>
      <a href="${fascia.sourceUrl}" target="_blank" rel="noreferrer">Official fascia details ↗</a>`;
  }

  function renderRoofIntelligence(): void {
    const container = query<HTMLElement>("[data-rv='roof-intelligence']");
    if (!packet) {
      container.replaceChildren();
      return;
    }
    const insights = buildRoofInsights(packet, gutter);
    const selected = insights.find((insight) => insight.id === focus) ?? insights[0];
    container.innerHTML = `
      <div class="rv-insight-copy" aria-live="polite">
        <span>${escapeHtml(selected.label)}</span>
        <strong>${escapeHtml(selected.value)}</strong>
        <p>${escapeHtml(selected.description)}</p>
      </div>
      <div class="rv-insight-tabs" role="group" aria-label="Explore roof details">
        ${insights.map((insight) => `<button type="button" data-action="focus" data-focus="${insight.id}" aria-pressed="${insight.id === focus}">${escapeHtml(insight.tabLabel)}</button>`).join("")}
      </div>`;
  }

  function renderPrice(): void {
    if (!packet) return;
    const reference = findProfile(DEFAULT_PROFILE_ID)!;
    const relative = calculateRelativePrice(packet.aggregates, profile, reference);
    const guide = shell === "standalone" ? calculateEstimateGuide(packet.aggregates, profile) : null;
    query<HTMLElement>("[data-rv='price']").innerHTML = `
      <div class="rv-price-main"><span>Relative material effect</span><strong>${escapeHtml(relative.label)}</strong></div>
      ${shell === "standalone"
        ? guide
          ? `<div class="rv-price-guide"><span>Indicative project guide</span><b>${formatGuideValue(guide.low)}–${formatGuideValue(guide.high)}</b></div>`
          : `<div class="rv-price-guide"><span>Indicative project guide</span><b>Pricing to be checked</b></div>`
        : ""}
      <p>${shell === "standalone"
        ? `The relative effect uses the current guide material rates and supplied ${packet.aggregates.surfaceAreaM2.toFixed(0)} m² roof aggregate. The overall guide also uses ${packet.aggregates.storeys} storey. Labour, access, scaffolding, removal, disposal, repairs, drainage, electrical work and additions are excluded unless stated. A guide, not a quote.`
        : `The relative effect compares material only against the reference profile on the supplied ${packet.aggregates.surfaceAreaM2.toFixed(0)} m² roof aggregate. It does not calculate or display a project quote. Authorised pricing remains in your Project Room.`}</p>`;
  }

  function renderCostFactors(): void {
    if (!packet) return;
    const reference = findProfile(DEFAULT_PROFILE_ID)!;
    const factors = buildCostFactors(packet.aggregates, profile, reference);
    query<HTMLElement>("[data-rv='cost-factors']").innerHTML = `
      <div class="rv-cost-head"><div><span>What shapes the job</span><strong>Understand the price</strong></div><p>Only the material comparison changes here.</p></div>
      <div class="rv-cost-list">
        ${factors.map((factor) => `<div class="rv-cost-factor">
          <span class="rv-cost-status rv-cost-${factor.status}">${factor.status === "updates" ? "Updates here" : factor.status === "mapped" ? "Mapped" : "Confirm"}</span>
          <div><b>${escapeHtml(factor.label)}</b><strong>${escapeHtml(factor.value)}</strong><p>${escapeHtml(factor.explanation)}</p></div>
        </div>`).join("")}
      </div>`;
  }

  function renderAdditions(): void {
    if (!packet) return;
    const additions = buildRoofAdditions(packet, gutter);
    query<HTMLElement>("[data-rv='additions']").innerHTML = additions.map((addition) => {
      const savedRequest = additionInterests.get(addition.id);
      const saved = Boolean(savedRequest) && (addition.id !== "gutters" || savedRequest?.gutter === gutter.id);
      return `<article class="rv-addition">
        <div class="rv-addition-head"><div><span>${escapeHtml(addition.label)}</span><strong>${escapeHtml(addition.name)}</strong></div><b>${saved ? "Saved" : "Optional"}</b></div>
        <p>${escapeHtml(addition.summary)}</p>
        <ul>${addition.facts.map((fact) => `<li>${escapeHtml(fact)}</li>`).join("")}</ul>
        <small>${escapeHtml(addition.disclosure)}</small>
        <button type="button" data-action="addition-request" data-addition="${addition.id}" class="${saved ? "is-saved" : ""}">${saved ? "✓ Interest saved" : addition.id === "solar" ? "Ask about solar" : addition.id === "skylight" ? "Ask about a skylight" : "Include gutters for review"}</button>
        ${saved ? `<em>${shell === "standalone" ? "Saved on this device only." : "Interest stated to your Project Room."} Your job has not been updated.</em>` : ""}
      </article>`;
    }).join("");
  }

  function renderDecision(): void {
    renderRecommendation();
    renderProducts();
    renderProfileDetail();
    renderColours();
    renderSampleRequest();
    renderGutters();
    renderFascias();
    renderPrice();
    renderCostFactors();
    renderAdditions();
    renderConfirmation();
    renderRoofIntelligence();
  }

  function setAppearanceInternal(nextProfile: Profile, nextColour: Colour, clearSaved = true): void {
    const changed = nextProfile.id !== profile.id || nextColour.id !== colour.id;
    profile = nextProfile;
    colour = nextColour;
    if (changed && clearSaved) clearConfirmation(true);
    scene?.applyAppearance(profile, colour);
    if (storyActive && packet) storyChapters = buildRoofStory(packet, profile, gutter, findProfile(DEFAULT_PROFILE_ID)!, fascia);
    renderDecision();
    if (storyActive) renderStory();
  }

  function setGutterInternal(nextGutter: GutterSpec): void {
    if (nextGutter.id === gutter.id) return;
    gutter = nextGutter;
    scene?.applyGutter(gutter);
    if (storyActive && packet) storyChapters = buildRoofStory(packet, profile, gutter, findProfile(DEFAULT_PROFILE_ID)!, fascia);
    renderGutters();
    renderRoofIntelligence();
    renderAdditions();
    if (storyActive) renderStory();
  }

  function setFasciaInternal(nextFascia: FasciaSpec): void {
    if (nextFascia.id === fascia.id) return;
    fascia = nextFascia;
    scene?.applyFascia(fascia);
    if (storyActive && packet) storyChapters = buildRoofStory(packet, profile, gutter, findProfile(DEFAULT_PROFILE_ID)!, fascia);
    renderFascias();
    if (storyActive) renderStory();
  }

  function setFocusInternal(nextFocus: RoofInsightId): void {
    focus = nextFocus;
    scene?.setFocus(focus);
    renderRoofIntelligence();
  }

  function showFeatureAnnotation(feature: { label: string; detail: string }): void {
    const note = query<HTMLElement>("[data-rv='feature-note']");
    note.hidden = false;
    note.innerHTML = `<strong>${escapeHtml(feature.label)}</strong><span>${escapeHtml(feature.detail)}</span>`;
  }

  function prefersReducedMotion(): boolean {
    const forced = import.meta.env?.DEV && new URLSearchParams(window.location.search).has("qaReducedMotion");
    return forced || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function renderStory(): void {
    const container = query<HTMLElement>("[data-rv='story']");
    const chapter = storyChapters[storyIndex];
    if (!storyActive || !chapter) {
      container.hidden = true;
      container.replaceChildren();
      return;
    }
    const progress = ((storyIndex + 1) / storyChapters.length) * 100;
    const manualOnly = prefersReducedMotion();
    container.hidden = false;
    container.innerHTML = `
      <div class="rv-story-progress" aria-hidden="true"><i style="width:${progress}%"></i></div>
      <div class="rv-story-meta"><span>${storyIndex + 1} of ${storyChapters.length}</span><button type="button" data-action="story-close" aria-label="Close guided roof story">Close</button></div>
      <div class="rv-story-value">${escapeHtml(chapter.value)}</div>
      <h2>${escapeHtml(chapter.title)}</h2>
      <p>${escapeHtml(chapter.body)}</p>
      <ul>${chapter.points.map((point) => `<li>${escapeHtml(point)}</li>`).join("")}</ul>
      <div class="rv-story-controls">
        <button type="button" data-action="story-back" ${storyIndex === 0 ? "disabled" : ""}>Back</button>
        ${manualOnly ? "" : `<button type="button" data-action="story-pause">${storyPaused ? "Resume" : "Pause"}</button>`}
        <button type="button" data-action="story-next" class="rv-story-next">${storyIndex === storyChapters.length - 1 ? "Finish exploring" : "Next"}</button>
      </div>`;
  }

  function scheduleStoryAdvance(): void {
    clearStoryTimer();
    if (!storyActive || storyPaused || prefersReducedMotion() || storyIndex >= storyChapters.length - 1) return;
    storyTimer = window.setTimeout(() => {
      if (storyTimer !== null) timers.delete(storyTimer);
      storyTimer = null;
      if (!storyActive || storyPaused) return;
      storyIndex += 1;
      runStoryChapter();
    }, 5600);
    timers.add(storyTimer);
  }

  function runStoryChapter(): void {
    if (!storyActive || !packet) return;
    const chapter = storyChapters[storyIndex];
    if (!chapter) return;
    setFocusInternal(chapter.focus);
    if (chapter.view) {
      currentView = chapter.view;
      scene?.setView(chapter.view);
      syncViewControls();
    }
    const stage = query<HTMLElement>("[data-rv='stage']");
    root.querySelector<HTMLElement>("[data-rv='blueprint']")?.remove();
    if (chapter.blueprint && !prefersReducedMotion()) {
      beginReveal(packet);
    } else {
      stage.dataset.phase = "roof";
      scene?.reveal(true);
    }
    renderStory();
    scheduleStoryAdvance();
  }

  function startStoryInternal(): void {
    if (!packet) return;
    storyChapters = buildRoofStory(packet, profile, gutter, findProfile(DEFAULT_PROFILE_ID)!, fascia);
    storyIndex = 0;
    storyActive = true;
    storyPaused = false;
    root.classList.add("rv-story-active");
    runStoryChapter();
  }

  function stopStoryInternal(): void {
    clearStoryTimer();
    storyActive = false;
    storyPaused = false;
    root.classList.remove("rv-story-active");
    const stage = query<HTMLElement>("[data-rv='stage']");
    root.querySelector<HTMLElement>("[data-rv='blueprint']")?.remove();
    stage.dataset.phase = "roof";
    scene?.reveal(true);
    scene?.reset();
    currentView = "perspective";
    syncViewControls();
    setFocusInternal("roof");
    renderStory();
  }

  function beginReveal(currentPacket: RoofvizPacket): void {
    const stage = query<HTMLElement>("[data-rv='stage']");
    if (prefersReducedMotion()) {
      stage.dataset.phase = "roof";
      scene?.reveal(true);
      return;
    }
    stage.insertAdjacentHTML("beforeend", renderBlueprint(currentPacket));
    stage.dataset.phase = "blueprint";
    schedule(() => {
      stage.dataset.phase = "lifting";
      scene?.reveal(false, () => { stage.dataset.phase = "roof"; });
    }, 1250);
    schedule(() => root.querySelector<HTMLElement>("[data-rv='blueprint']")?.remove(), 2400);
  }

  function preparePacket(nextPacket: RoofvizPacket | null): void {
    emitStatus({ state: "preparing" });
    clearTimers();
    storyActive = false;
    storyPaused = false;
    storyIndex = 0;
    storyChapters = [];
    root.classList.remove("rv-story-active");
    query<HTMLElement>("[data-rv='story']").hidden = true;
    scene?.destroy();
    scene = null;
    currentCanvas?.remove();
    currentCanvas = null;
    packet = nextPacket;
    const restoredSession = initialSessionToRestore;
    initialSessionToRestore = null;
    gutter = findGutter(restoredSession?.gutter ?? presentation.gutterId) ?? gutter;
    fascia = findFascia(restoredSession?.fascia ?? presentation.fasciaId) ?? fascia;
    currentView = restoredSession?.view ?? currentView;
    syncViewControls();
    focus = "roof";
    const propertyLabel = root.querySelector<HTMLElement>("[data-rv='property']");
    if (propertyLabel) propertyLabel.textContent = packet?.property.label ?? "No roof supplied";

    if (!packet) {
      renderRoofIntelligence();
      renderFallback(
        "No roof has been supplied",
        "Ask your roofing team to add the mapped roof to this visualisation.",
        "no_packet",
      );
      return;
    }
    const validation = validatePacketForRendering(packet);
    if (!validation.ok) {
      const fallback = fallbackForValidation(validation);
      query<HTMLElement>("[data-rv='roof-intelligence']").replaceChildren();
      const reason = validation.errors.some((error) => error.code === "unsupported_packet")
        ? "unsupported_packet"
        : "invalid_packet";
      renderFallback(fallback.title, fallback.message, reason);
      return;
    }

    root.classList.remove("rv-is-unavailable");

    recommendation = recommendProfile(options.recommendation, packet.aggregates);
    const stored = parsePreference(
      readDeviceItem(preferenceStorageKey(packet.property.trackingId)),
      packet.property.trackingId,
    );
    const suppliedPreference = validInitialPreference(options.initialPreference, packet);
    confirmedPreference = suppliedPreference ?? stored;
    if (suppliedPreference) {
      writeDeviceItem(preferenceStorageKey(packet.property.trackingId), serializePreference(suppliedPreference));
    }
    const restoredProfile = restoredSession ? findProfile(restoredSession.appearance.profile) : null;
    const restoredColour = restoredSession ? findColour(restoredSession.appearance.colour) : null;
    const initialProfileId = restoredProfile?.id ?? confirmedPreference?.profile ?? recommendation.initialProfileId;
    profile = findProfile(initialProfileId) ?? findProfile(DEFAULT_PROFILE_ID)!;
    const initialColour = restoredColour && profile.supportedColourIds.includes(restoredColour.id)
      ? restoredColour
      : findColour(confirmedPreference?.colour ?? DEFAULT_COLOUR_ID);
    colour = initialColour && profile.supportedColourIds.includes(initialColour.id)
      ? initialColour
      : coloursForProfile(profile)[0];
    sampleRequest = parseSampleRequest(
      readDeviceItem(sampleRequestStorageKey(packet.property.trackingId)),
      packet.property.trackingId,
    );
    additionInterests.clear();
    (["solar", "skylight", "gutters"] as RoofAdditionId[]).forEach((addition) => {
      const saved = parseAdditionInterest(
        readDeviceItem(additionInterestStorageKey(packet!.property.trackingId, addition)),
        packet!.property.trackingId,
        addition,
      );
      if (saved) additionInterests.set(addition, saved);
    });

    const assumption = presentation.assumedHeightLabel ?? assumptionLabel({
      assumedHeights: presentation.assumedHeights,
      pitchSources: packet.planes.map((plane) => plane.pitchSource),
    });
    const assumptionElement = query<HTMLElement>("[data-rv='assumption']");
    assumptionElement.hidden = !assumption;
    assumptionElement.textContent = assumption ? `${assumption}${presentation.homeownerNote ? ` ${presentation.homeownerNote}` : ""}` : "";
    renderDecision();

    const stage = query<HTMLElement>("[data-rv='stage']");
    stage.replaceChildren();
    const pitchValues = [...new Set(packet.planes.map((plane) => plane.pitchDeg))];
    const pitchDescription = pitchValues.length === 1
      ? `${pitchValues[0].toFixed(1)} degree pitch`
      : `${pitchValues.length} supplied pitch values`;
    query<HTMLElement>("[data-rv='canvas-description']").textContent =
      `Mapped roof for ${packet.property.label}: ${packet.aggregates.surfaceAreaM2.toFixed(0)} square metres, ${packet.planes.length} roof planes, ${pitchDescription}. Use the labelled view, profile, colour and gutter controls to explore the same roof.`;
    currentCanvas = document.createElement("canvas");
    currentCanvas.className = "rv-canvas";
    currentCanvas.setAttribute("role", "img");
    currentCanvas.setAttribute("aria-label", `3D roof view for ${packet.property.label}`);
    currentCanvas.setAttribute("aria-describedby", canvasDescriptionId);
    currentCanvas.textContent = query<HTMLElement>("[data-rv='canvas-description']").textContent;
    stage.append(currentCanvas);
    try {
      const fallbackProbe = import.meta.env?.DEV
        ? new URLSearchParams(window.location.search).get("qaFallback")
        : null;
      if (fallbackProbe === "render-failure") {
        throw new Error("Development-only render failure probe");
      }
      if (fallbackProbe === "webgl") {
        throw new Error("Development-only WebGL graphics context unavailable probe");
      }
      scene = new RoofVizScene({
        canvas: currentCanvas,
        container: stage,
        packet,
        solved: validation.solved,
        footprints: presentation.footprints ?? footprintsFromPacket(packet),
        profile,
        colour,
        gutter,
        fascia,
        northOffsetDeg: presentation.northOffsetDeg,
        autoRotate: presentation.autoRotate,
        autoRotateSpeed: presentation.autoRotateSpeed,
        prefersReducedMotion,
        onFeatureSelected: showFeatureAnnotation,
      });
      scene.setFocus(focus);
      if (currentView !== "perspective") scene.setView(currentView);
      if (presentation.startInRoof) {
        stage.dataset.phase = "roof";
        scene.reveal(true);
      } else {
        beginReveal(packet);
      }
      emitStatus({ state: "ready" });
    } catch (error) {
      scene?.destroy();
      scene = null;
      currentCanvas.remove();
      currentCanvas = null;
      const message = error instanceof Error ? error.message : String(error);
      const reason = /webgl|graphics context/i.test(message) ? "webgl_unavailable" : "render_failed";
      renderFallback(
        "The roof view could not start",
        "Please refresh the page or try a device with 3D graphics enabled.",
        reason,
      );
    }
  }

  async function capturePosterInternal(): Promise<Blob> {
    if (!packet || !scene || !currentCanvas) throw new Error("The roof poster is not available until the roof has rendered.");
    scene.renderNow();
    const snapshot = currentCanvas.toDataURL("image/png");
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const value = new Image();
      value.addEventListener("load", () => resolve(value), { once: true });
      value.addEventListener("error", () => reject(new Error("Unable to read the current roof view.")), { once: true });
      value.src = snapshot;
    });
    const state = posterCaptionState(packet.property.label, profile.name, colour.name);
    const compose = document.createElement("canvas");
    compose.width = POSTER_WIDTH;
    compose.height = POSTER_HEIGHT;
    const context = compose.getContext("2d");
    if (!context) throw new Error("Unable to prepare the roof poster.");

    context.fillStyle = "#f4f5f0";
    context.fillRect(0, 0, POSTER_WIDTH, POSTER_HEIGHT);
    context.fillStyle = "#102c24";
    context.fillRect(0, 0, POSTER_WIDTH, 94);
    context.fillStyle = "#ffffff";
    context.font = '700 26px "Manrope", system-ui, sans-serif';
    context.fillText("ROOFING", 58, 43);
    context.fillStyle = "#f4ce2a";
    context.fillRect(55, 53, 182, 25);
    context.fillStyle = "#242722";
    context.font = '800 18px "Manrope", system-ui, sans-serif';
    context.fillText("INDUSTRIES", 67, 73);
    context.fillStyle = "rgba(255,255,255,.72)";
    context.font = '600 20px "Manrope", system-ui, sans-serif';
    context.fillText("Your digital roof", 280, 60);
    context.textAlign = "right";
    context.fillStyle = "#ffffff";
    context.font = '650 22px "Manrope", system-ui, sans-serif';
    context.fillText(state.propertyLabel, POSTER_WIDTH - 58, 60);
    context.textAlign = "left";

    const viewTop = 94;
    const viewHeight = 650;
    const scale = Math.max(POSTER_WIDTH / image.naturalWidth, viewHeight / image.naturalHeight);
    const drawWidth = image.naturalWidth * scale;
    const drawHeight = image.naturalHeight * scale;
    context.drawImage(image, (POSTER_WIDTH - drawWidth) / 2, viewTop + (viewHeight - drawHeight) / 2, drawWidth, drawHeight);

    context.fillStyle = "#ffffff";
    context.fillRect(0, 744, POSTER_WIDTH, 156);
    context.fillStyle = "#016c46";
    context.font = '750 18px "Manrope", system-ui, sans-serif';
    context.fillText("YOUR CURRENT LOOK", 58, 786);
    context.fillStyle = "#143a2e";
    context.font = '650 36px "Manrope", system-ui, sans-serif';
    context.fillText(`${state.profileName} · ${state.colourName}`, 58, 829);
    context.fillStyle = "#65736c";
    context.font = '500 17px "Manrope", system-ui, sans-serif';
    context.fillText(`${state.disclaimer} · ${state.dateIso}`, 58, 868);

    return new Promise<Blob>((resolve, reject) => {
      compose.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Unable to export the roof poster.")), "image/png");
    });
  }

  async function saveCurrentView(): Promise<void> {
    if (shell !== "standalone") return;
    const buttons = root.querySelectorAll<HTMLButtonElement>("[data-action='save-view']");
    root.dataset.posterState = "preparing";
    delete root.dataset.posterBytes;
    buttons.forEach((button) => { button.disabled = true; button.textContent = "Preparing poster…"; });
    try {
      const blob = await capturePosterInternal();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "roofing-industries-roof-view.png";
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      root.dataset.posterState = "saved";
      root.dataset.posterBytes = String(blob.size);
      buttons.forEach((button) => { button.textContent = "Poster saved"; });
    } catch {
      root.dataset.posterState = "failed";
      buttons.forEach((button) => { button.textContent = "Could not save"; });
    }
    schedule(() => buttons.forEach((button) => {
      button.disabled = false;
      button.textContent = button.classList.contains("rv-mobile-save-poster") ? "Save this roof view" : "Save view";
    }), 1800);
  }

  listen(root, "click", (event) => {
    const target = (event.target as Element | null)?.closest<HTMLElement>("[data-action]");
    if (!target || !root.contains(target)) return;
    const action = target.dataset.action;
    if (action === "profile") {
      const nextProfile = findProfile(target.dataset.profile);
      if (!nextProfile) return;
      const available = coloursForProfile(nextProfile);
      const nextColour = available.find((item) => item.id === colour.id) ?? available[0];
      setAppearanceInternal(nextProfile, nextColour);
    } else if (action === "colour") {
      const nextColour = findColour(target.dataset.colour);
      if (nextColour && profile.supportedColourIds.includes(nextColour.id)) setAppearanceInternal(profile, nextColour);
    } else if (action === "gutter") {
      const nextGutter = findGutter(target.dataset.gutter);
      if (nextGutter) setGutterInternal(nextGutter);
    } else if (action === "fascia") {
      const nextFascia = findFascia(target.dataset.fascia);
      if (nextFascia) setFasciaInternal(nextFascia);
    } else if (action === "toggle-profiles") {
      catalogueExpanded = !catalogueExpanded;
      renderProducts();
    } else if (action === "toggle-gutters") {
      gutterCatalogueExpanded = !gutterCatalogueExpanded;
      renderGutters();
    } else if (action === "focus") {
      const nextFocus = target.dataset.focus as RoofInsightId;
      if (["roof", "pitch", "junctions", "rainwater"].includes(nextFocus)) setFocusInternal(nextFocus);
    } else if (action === "story-start") {
      startStoryInternal();
    } else if (action === "story-close") {
      stopStoryInternal();
    } else if (action === "story-back") {
      if (!storyActive || storyIndex === 0) return;
      storyIndex -= 1;
      runStoryChapter();
    } else if (action === "story-next") {
      if (!storyActive) return;
      if (storyIndex >= storyChapters.length - 1) stopStoryInternal();
      else {
        storyIndex += 1;
        runStoryChapter();
      }
    } else if (action === "story-pause") {
      if (!storyActive) return;
      storyPaused = !storyPaused;
      renderStory();
      if (storyPaused) clearStoryTimer();
      else scheduleStoryAdvance();
    } else if (action === "view") {
      currentView = target.dataset.view as CardinalView;
      scene?.setView(currentView);
      syncViewControls();
    } else if (action === "zoom-in") {
      scene?.zoom(0.84);
    } else if (action === "zoom-out") {
      scene?.zoom(1.18);
    } else if (action === "reset") {
      scene?.reset();
      currentView = "perspective";
      syncViewControls();
      setFocusInternal("roof");
    } else if (action === "save-view") {
      void saveCurrentView();
    } else if (action === "sample-request" && packet) {
      const request = createSampleRequest(packet.property.trackingId, profile.id, colour.id);
      writeDeviceItem(sampleRequestStorageKey(packet.property.trackingId), serializeInterest(request));
      sampleRequest = request;
      renderSampleRequest();
      emitIntent({
        schema: "roofviz_intent_v1",
        type: "sample_interest_stated",
        trackingId: packet.property.trackingId,
        interest: request,
      });
    } else if (action === "addition-request" && packet) {
      const addition = target.dataset.addition as RoofAdditionId;
      if (!["solar", "skylight", "gutters"].includes(addition)) return;
      const request = createAdditionInterest(packet.property.trackingId, addition, addition === "gutters" ? gutter.id : undefined);
      writeDeviceItem(additionInterestStorageKey(packet.property.trackingId, addition), serializeInterest(request));
      additionInterests.set(addition, request);
      renderAdditions();
      emitIntent({
        schema: "roofviz_intent_v1",
        type: "addition_interest_stated",
        trackingId: packet.property.trackingId,
        interest: request,
      });
    } else if (action === "confirm" && packet) {
      const preference = createPreference(packet.property.trackingId, profile.id, colour.id);
      writeDeviceItem(preferenceStorageKey(packet.property.trackingId), serializePreference(preference));
      confirmedPreference = preference;
      renderConfirmation();
      emitIntent({
        schema: "roofviz_intent_v1",
        type: "appearance_preference_stated",
        trackingId: packet.property.trackingId,
        preference,
      });
    }
  });

  preparePacket(packet);

  return {
    loadPacket(nextPacket, nextPresentation = presentation): void {
      if (destroyed) return;
      presentation = { ...nextPresentation, shell };
      preparePacket(nextPacket);
    },
    setAppearance(appearance): void {
      if (destroyed) return;
      const nextProfile = findProfile(appearance.profile);
      const nextColour = findColour(appearance.colour);
      if (!nextProfile || !nextColour || !nextProfile.supportedColourIds.includes(nextColour.id)) return;
      setAppearanceInternal(nextProfile, nextColour);
    },
    setGutter(nextGutterId): void {
      if (destroyed) return;
      const nextGutter = findGutter(nextGutterId);
      if (nextGutter) setGutterInternal(nextGutter);
    },
    setFascia(nextFasciaId): void {
      if (destroyed) return;
      const nextFascia = findFascia(nextFasciaId);
      if (nextFascia) setFasciaInternal(nextFascia);
    },
    setView(view): void {
      if (destroyed) return;
      currentView = view;
      scene?.setView(view);
      syncViewControls();
    },
    startStory(): void { if (!destroyed) startStoryInternal(); },
    stopStory(): void { if (!destroyed) stopStoryInternal(); },
    reset(): void {
      if (destroyed) return;
      if (storyActive) stopStoryInternal();
      else {
        scene?.reset();
        currentView = "perspective";
        syncViewControls();
        setFocusInternal("roof");
      }
    },
    capturePoster(): Promise<Blob> {
      if (destroyed) return Promise.reject(new Error("The roof visualizer has been destroyed."));
      return capturePosterInternal();
    },
    snapshot(): RoofVisualizerSession {
      return {
        appearance: { profile: profile.id, colour: colour.id },
        gutter: gutter.id,
        fascia: fascia.id,
        view: currentView,
      };
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      clearTimers();
      scene?.destroy();
      scene = null;
      currentCanvas?.remove();
      currentCanvas = null;
      abortController.abort();
      roofVizDiagnostics.listener(-listenerCount);
      listenerCount = 0;
      root.replaceChildren();
      root.classList.remove("roofviz-app", `rv-shell-${shell}`, "rv-is-unavailable", "rv-story-active");
      delete root.dataset.posterState;
      delete root.dataset.posterBytes;
      roofVizDiagnostics.mounted(-1);
    },
  };
}
